/**
 * CATALOG GROUNDING for a generation run (feature 0059 — founder 2026-08-21: «vous récupérez ça
 * d'où ?» pointing at the theme's fake BERQ demo products).
 *
 * A theme workspace ships sections whose product/collection pickers are unbound (or bound to the
 * theme's own demo refs), and the builder canvas — unlike the authoritative storefront, which HIDES
 * them — falls back to the demo catalog so the seller can design against something. During an AI
 * generation that fallback IS template content, and the virgin-start doctrine forbids it. This module
 * PLANS the correction for one page, purely:
 *
 *  - every product-picker (block-level `ref` of a `product` block, or a section-level field) is bound
 *    to one of the store's REAL active products — the CHOSEN product for section-level features, the
 *    rest of the catalog round-robin for grids;
 *  - a catalog-driven section that cannot be grounded (no other active product for a related grid, or
 *    a collection picker — the edit canvas has no collection-resolution pipeline, so a bound
 *    collection still paints demo products) is DISABLED for the run: an honest absence instead of a
 *    fabricated shelf. The live storefront would have hidden it anyway.
 *
 * Pure: document + registry + catalog in, actions out. The store layer applies them through the
 * normal edit paths (updateBlockSettings / updateSectionSettings / toggleSectionDisabled).
 */
import type { PageDocument, SectionRegistry } from '@aquilixshop/ui-react';

export interface GroundingActions {
  bindBlock: { sectionId: string; blockId: string; fieldId: string; productId: string }[];
  bindSection: { sectionId: string; fieldId: string; productId: string }[];
  /**
   * Picker blocks to CREATE before binding (a freshly inserted grid has ZERO cards — the 2026-08-21
   * «À découvrir aussi» BERQ shelf slipped through because the plan only counted instantiated
   * blocks). The caller adds them, then RE-PLANS: the second pass sees the new blocks and binds.
   */
  addBlocks: { sectionId: string; blockType: string; count: number }[];
  /** Sections to disable for the run — currently enabled, catalog-driven, and ungroundable. */
  disable: string[];
}

/** Cards a grounded grid gets — enough to read as a shelf, never more than the pool can fill uniquely. */
const GRID_CARDS = 4;

interface SchemaField {
  id: string;
  type: string;
}
interface SchemaChild {
  type: string;
  fields?: readonly SchemaField[];
}

export function planCatalogGrounding(
  document: PageDocument,
  registry: SectionRegistry,
  activeProductIds: readonly string[],
  chosenProductId: string | null,
): GroundingActions {
  const actions: GroundingActions = { bindBlock: [], bindSection: [], addBlocks: [], disable: [] };
  // Grids show the catalog AROUND the chosen product — repeating the page's own product in its
  // "you may also like" grid reads broken. Fall back to the whole catalog when it is the only one.
  const others = activeProductIds.filter((id) => id !== chosenProductId);
  const pool = others.length > 0 ? others : [];
  let cursor = 0;

  for (const section of document.sections ?? []) {
    if (!section || typeof section.id !== 'string' || section.disabled) continue;
    const schema = registry[section.type]?.schema as
      | { fields?: readonly SchemaField[]; blocks?: readonly SchemaChild[] }
      | undefined;
    if (!schema) continue;

    let pickers = 0;
    let bound = 0;

    // Block-level product pickers (the related-products model: repeatable `product` blocks).
    for (const block of section.blocks ?? []) {
      if (!block || block.disabled || typeof block.id !== 'string') continue;
      const child = schema.blocks?.find((b) => b.type === block.type);
      const refField = child?.fields?.find((f) => f.type === 'product-picker');
      if (!refField) continue;
      pickers += 1;
      const pick = pool[cursor % Math.max(1, pool.length)];
      if (pool.length > 0 && pick) {
        cursor += 1;
        actions.bindBlock.push({
          sectionId: section.id,
          blockId: block.id,
          fieldId: refField.id,
          productId: pick,
        });
        bound += 1;
      }
    }

    // Section-level product pickers (featured-product): the CHOSEN product is the one the store is
    // built around — it takes every section-level feature slot.
    for (const f of schema.fields ?? []) {
      if (f.type !== 'product-picker') continue;
      pickers += 1;
      const pick = chosenProductId ?? activeProductIds[0] ?? null;
      if (pick) {
        actions.bindSection.push({ sectionId: section.id, fieldId: f.id, productId: pick });
        bound += 1;
      }
    }

    // A grid whose SCHEMA declares product-picker child blocks but which has NONE instantiated (a
    // fresh from-scratch insert): the plan must CREATE the cards, or the renderer falls back to the
    // demo shelf. With products in the pool → addBlocks (the re-plan binds them); with an empty
    // pool → it counts as an unbindable picker and the section is disabled below.
    const productChild = (schema.blocks ?? []).find((b) =>
      (b.fields ?? []).some((f) => f.type === 'product-picker'),
    );
    if (productChild) {
      const instantiated = (section.blocks ?? []).filter(
        (b) => b && !b.disabled && b.type === productChild.type,
      ).length;
      if (instantiated === 0) {
        pickers += 1;
        if (pool.length > 0) {
          actions.addBlocks.push({
            sectionId: section.id,
            blockType: productChild.type,
            count: Math.min(GRID_CARDS, pool.length),
          });
          bound += 1; // the re-plan will bind the created blocks — do not disable this section
        }
      }
    }

    // Collection pickers: ungroundable in the edit canvas (see module doc) — they count as pickers
    // and bind nothing, so a purely collection-driven section falls through to disable.
    const hasCollectionPicker =
      (schema.fields ?? []).some((f) => f.type === 'collection-picker') ||
      (schema.blocks ?? []).some((b) =>
        (b.fields ?? []).some((f) => f.type === 'collection-picker'),
      );
    if (hasCollectionPicker) pickers += 1;

    if (pickers > 0 && bound === 0) actions.disable.push(section.id);
  }
  return actions;
}
